import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** 手机端用底部抽屉（sheet），桌面端用居中弹窗（center） */
  variant?: 'center' | 'sheet';
  size?: 'md' | 'lg';
}

/**
 * 精简版弹窗。
 *
 * 平台前端的浮层系统还带「从触发按钮飞出」的 FLIP 过渡，那套定位逻辑与平台强耦合；
 * 这里只保留必要的部分：焦点陷阱、Esc 关闭、滚动锁、关闭后焦点归位。
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  variant = 'center',
  size = 'md',
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  /*
   * `onClose` 只用于事件回调，必须放进 ref 而不是 effect 依赖。
   *
   * 调用方几乎都传内联箭头函数（`onClose={() => setOpen(false)}`），
   * 一旦它进了依赖数组，父组件每次重渲染 —— 包括「输入框每敲一个字触发的 setState」——
   * 都会重跑下面的 effect，于是初始聚焦又被执行一次，光标当场被抢到第一个可聚焦元素（关闭按钮）上。
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    const focusables = () =>
      panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
    const firstField = () =>
      panel?.querySelector<HTMLElement>(
        'textarea:not([disabled]), select:not([disabled]), input:not([disabled]):not([type="hidden"])',
      ) ?? null;
    const firstFooterControl = () => footerRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? null;

    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // 初始焦点只在「打开」这一刻决定一次：优先第一个输入框，其次页脚按钮，最后兜底到面板本身。
    // 不选第一个可聚焦元素是因为那是标题栏的关闭按钮，打开就被聚焦并不符合预期。
    const target = firstField() ?? firstFooterControl() ?? focusables()[0] ?? panel;
    // 等一帧，确保弹窗已经挂载并完成布局
    const raf = window.requestAnimationFrame(() => target?.focus());

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        panel?.focus();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === firstItem || active === panel)) {
        event.preventDefault();
        lastItem?.focus();
      } else if (!event.shiftKey && active === lastItem) {
        event.preventDefault();
        firstItem?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      restoreRef.current?.focus?.();
    };
    // 只依赖 `open`：见上方对 onCloseRef 的说明，这里绝不能把回调放进依赖。
  }, [open]);

  if (!open) return null;

  const panelSize = size === 'lg' ? 'md:max-w-2xl' : 'md:max-w-lg';

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-end justify-center md:items-center">
      <div
        className="absolute inset-0 bg-[rgb(2_6_23_/_0.55)]"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={[
          'relative flex max-h-[92dvh] w-full flex-col border border-line bg-surface-1 shadow-lg',
          'rounded-t-2xl md:rounded-lg',
          panelSize,
          variant === 'sheet' ? 'pb-[env(safe-area-inset-bottom)]' : '',
        ].join(' ')}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-fg">{title}</h2>
            {description ? (
              <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="-mt-1 -mr-1 shrink-0 rounded px-2 py-1 text-sm text-fg-subtle hover:bg-surface-3 hover:text-fg"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">{children}</div>

        {footer ? (
          <div
            ref={footerRef}
            className="flex items-center justify-end gap-2 border-t border-line px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
